import type { BrandWarning } from '@innovationdepartment/proxima-sdk-axios';
import { useProximaSDK } from 'hooks';
import { useEffect, useState } from 'react';
import { useBrandStore } from 'stores';
import type { BrandWarningsPagination } from 'types/brandOverview';

function useBrandWarnings(pageSize: number = 25) {
  const [loading, setLoading] = useState(false);
  const { brand } = useBrandStore();

  const [brandWarnings, setBrandWarnings] = useState<BrandWarning[]>([]);
  const [pagination, setPagination] = useState<BrandWarningsPagination>({
    pageSize,
    pageNumber: 1,
    total: 0
  });

  const brandsApi = useProximaSDK('BrandsApi');

  const fetchBrandWarnings = async (pageNumber: number = 1) => {
    if (!brand?.brandId) return;
    setLoading(true);
    try {
      const response = await brandsApi.getBrandWarnings({
        brandId: brand.brandId,
        pageSize: pagination.pageSize,
        pageNumber
      });

      setBrandWarnings(response.data.brandWarnings);
      setPagination({
        pageSize: response.data.pageSize,
        pageNumber: response.data.pageNumber,
        total: response.data.total
      });
    } catch {
      /*  */
    }
    setLoading(false);
  };

  const goToNextPage = () => {
    const nextPage = pagination.pageNumber + 1;
    const maxPage = Math.ceil(pagination.total / pagination.pageSize);
    if (nextPage <= maxPage) {
      fetchBrandWarnings(nextPage);
    }
  };

  const goToPrevPage = () => {
    const prevPage = pagination.pageNumber - 1;
    if (prevPage >= 1) {
      fetchBrandWarnings(prevPage);
    }
  };

  /* fetch brand errors */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    fetchBrandWarnings();
  }, []);

  return {
    brandWarnings,
    pagination,
    fetchBrandWarnings,
    goToNextPage,
    goToPrevPage,
    brand,
    loading
  };
}

export default useBrandWarnings;
